/**
 * Comprehensive API Service for LeChalo Website
 * Industry-standard implementation with proper error handling, caching, and type safety
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import {
  ApiResponse,
  ApiErrorResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  User,
  Order,
  CreateOrderRequest,
  Bid,
  ApproveBidRequest,
  Location,
  SearchPlacesRequest,
  SearchPlacesResponse,
  GetAddressFromLatLngRequest,
  GetAddressFromLatLngResponse,
  VehicleType,
  VehicleSubType,
  Vehicle,
  Brand,
  VehicleModel,
  GoodsType,
  City,
  State,
  Country,
  Address,
  Payment,
  WalletTransaction,
  AddMoneyRequest,
  Feedback,
  SubmitFeedbackRequest,
  Notification,
  SendNotificationRequest,
  Shuttle,
  ShuttleBooking,
  Tour,
  TourDestination,
  OnCallPackage,
  OnCallBooking,
  Banner,
  FAQ,
  CancellationReason,
  CancelOrderRequest,
  DriverLocationUpdate,
  QueryParams,
  PaginationParams,
  ShuttleScheduleTime,
  ShuttleSchedule,
  ShuttleSeatSelection,
  ShuttlePickupPoint,
  ShuttleCouponCheckResponse,
  ShuttleBookingRequest,
  ShuttleBookingResponse,
} from '../types/api';
import showToast from '../utils/toastUtils';

// ============================================================================
// API CONFIGURATION
// ============================================================================

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://admin.lechalo.co.in/api/v1';
const API_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// API CLIENT CLASS
// ============================================================================

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
    this.loadTokenFromStorage();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // Add request timestamp for debugging
        (config as any).metadata = { startTime: Date.now() };

        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response time
        const endTime = Date.now();
        const startTime = (response.config as any).metadata?.startTime;
        if (startTime) {
          console.log(
            `API call to ${response.config.url} took ${endTime - startTime}ms`
          );
        }

        return response;
      },
      (error) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleError(error: any): void {
    console.error('[API Client] Error Handler:', error);
    console.error('[API Client] Error Response:', error?.response);
    console.error('[API Client] Error Request:', error?.request);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`[API Client] HTTP Error ${status}:`, data);

      switch (status) {
        case 401:
          this.handleUnauthorized();
          break;
        case 403:
          showToast.error('Access denied. Please check your permissions.');
          break;
        case 404:
          showToast.error('Resource not found.');
          break;
        case 422:
          this.handleValidationError(data);
          break;
        case 500:
          showToast.error('Server error. Please try again later.');
          break;
        default:
          showToast.error(data?.message || 'An unexpected error occurred.');
      }
    } else if (error.request) {
      console.error(
        '[API Client] Network Error - No response received:',
        error.request
      );
      showToast.error('Network error. Please check your connection.');
    } else {
      console.error('[API Client] Unknown Error:', error.message);
      showToast.error('An unexpected error occurred.');
    }
  }

  private handleUnauthorized(): void {
    this.clearToken();
    // Redirect to login page
    window.location.href = '/auth/login';
  }

  private handleValidationError(data: any): void {
    if (data?.errors) {
      const errorMessages = Object.values(data.errors).flat();
      errorMessages.forEach((message: any) => {
        showToast.error(message);
      });
    } else {
      showToast.error(data?.message || 'Validation error occurred.');
    }
  }

  private loadTokenFromStorage(): void {
    this.token = localStorage.getItem('authToken');
  }

  public setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  public clearToken(): void {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
  }

  // ============================================================================
  // GENERIC HTTP METHODS
  // ============================================================================

  private async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  private async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    console.log(`[API Client] POST ${url}`, data);
    try {
      const response = await this.client.post<ApiResponse<T>>(
        url,
        data,
        config
      );
      console.log(`[API Client] POST ${url} Response:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`[API Client] POST ${url} Error:`, error);
      console.error(`[API Client] Error response:`, error?.response?.data);
      console.error(`[API Client] Error status:`, error?.response?.status);
      throw error;
    }
  }

  private async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  private async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // ============================================================================
  // AUTHENTICATION API
  // ============================================================================

  async sendOtp(data: SendOtpRequest): Promise<ApiResponse<SendOtpResponse>> {
    return this.post<SendOtpResponse>('/sendotp', data);
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/login', data);
    return response.data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.client.post<RegisterResponse>(
      '/register',
      data
    );
    return response.data;
  }

  async verifyOtp(
    data: VerifyOtpRequest
  ): Promise<ApiResponse<VerifyOtpResponse>> {
    return this.post<VerifyOtpResponse>('/verify', data);
  }

  async logout(): Promise<ApiResponse<void>> {
    return this.get<void>('/logout');
  }

  async getMe(): Promise<ApiResponse<User>> {
    return this.get<User>('/customer/info');
  }

  async switchAccount(data: {
    from_role: string;
    to_role: string;
  }): Promise<ApiResponse<LoginResponse>> {
    return this.post<LoginResponse>('/auth/switch-account', data);
  }

  async updatePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<ApiResponse<void>> {
    return this.post<void>('/updatePassword', data);
  }

  // ============================================================================
  // USER PROFILE API
  // ============================================================================

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    // Backend expects PUT for profile updates
    console.log('[API Client] updateProfile called with:', data);
    return this.put<User>('/users/update', data);
  }

  async getUserDetails(): Promise<ApiResponse<User>> {
    return this.get<User>('/users/user_details');
  }

  async deleteAccount(): Promise<ApiResponse<void>> {
    return this.post<void>('/users/account-delete');
  }

  // ============================================================================
  // VEHICLE API
  // ============================================================================

  async getVehicleTypes(): Promise<ApiResponse<VehicleType[]>> {
    return this.post<VehicleType[]>('/vehicle-type');
  }

  async getVehicleTypesWithLocation(data: {
    lat: number;
    lng: number;
  }): Promise<ApiResponse<VehicleType[]>> {
    return this.post<VehicleType[]>('/vehicle-type', data);
  }

  async getVehicleSubTypes(
    vehicleTypeId: number
  ): Promise<ApiResponse<VehicleSubType[]>> {
    return this.post<VehicleSubType[]>('/vehicle/sub-type', {
      vehicle_type_id: vehicleTypeId,
    });
  }

  async getBrands(vehicleTypeId?: number): Promise<ApiResponse<Brand[]>> {
    const url = vehicleTypeId
      ? `/brands/get-brand-by-vehicletype-id`
      : '/brands';
    const data = vehicleTypeId ? { vehicle_type_id: vehicleTypeId } : undefined;
    return this.post<Brand[]>(url, data);
  }

  async getModels(brandId: number): Promise<ApiResponse<VehicleModel[]>> {
    return this.post<VehicleModel[]>('/models/get-model-by-brand-id', {
      brand_id: brandId,
    });
  }

  async getVehicles(): Promise<ApiResponse<Vehicle[]>> {
    return this.get<Vehicle[]>('/vehicle');
  }

  async addVehicle(data: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> {
    return this.post<Vehicle>('/vehicle', data);
  }

  async updateVehicle(
    id: number,
    data: Partial<Vehicle>
  ): Promise<ApiResponse<Vehicle>> {
    return this.post<Vehicle>(`/vehicle/update-vehicle/${id}`, data);
  }

  async deleteVehicle(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/vehicle/${id}`);
  }

  async selectVehicle(vehicleId: number): Promise<ApiResponse<void>> {
    return this.post<void>('/vehicle/select-vehicle', {
      vehicle_id: vehicleId,
    });
  }

  // ============================================================================
  // LOCATION API
  // ============================================================================

  async searchPlaces(
    data: SearchPlacesRequest
  ): Promise<ApiResponse<SearchPlacesResponse>> {
    return this.get<SearchPlacesResponse>('/location/search-places', {
      params: data,
    });
  }

  async getAddressFromLatLng(
    data: GetAddressFromLatLngRequest
  ): Promise<ApiResponse<GetAddressFromLatLngResponse>> {
    return this.get<GetAddressFromLatLngResponse>(
      '/location/get-address-from-lat-lng',
      { params: data }
    );
  }

  async getDirections(data: {
    origin: string;
    destination: string;
  }): Promise<ApiResponse<any>> {
    return this.post<any>('/location/get-directions-from-lat-lng', data);
  }

  /**
   * Get directions/route from lat/lng coordinates
   * Mobile app compatible implementation
   * @param data - fromlatlng: string "lng,lat", tolatlng: array of strings ["lng,lat", ...]
   * @returns GeoJSON features with route coordinates
   */
  async getDirectionsFromLatLng(data: {
    fromlatlng: string; // Format: "lng,lat" (e.g., "72.5587664,23.0286188")
    tolatlng: string[]; // Array of strings in format: "lng,lat"
  }): Promise<ApiResponse<{
    features: Array<{
      geometry: {
        coordinates: Array<[number, number]>; // GeoJSON format: [lng, lat]
        type: string;
      };
      properties: any;
      type: string;
    }>;
    type: string;
  }>> {
    console.log('[API] getDirectionsFromLatLng - Request payload:', JSON.stringify(data, null, 2));
    const response = await this.post<any>('/location/get-directions-from-lat-lng', data);
    console.log('[API] getDirectionsFromLatLng - Response:', JSON.stringify(response, null, 2));
    return response;
  }

  async getCities(): Promise<ApiResponse<City[]>> {
    return this.get<City[]>('/get-cities');
  }

  async getStates(countryId: number): Promise<ApiResponse<State[]>> {
    return this.get<State[]>(`/state/${countryId}`);
  }

  async getCountries(): Promise<ApiResponse<Country[]>> {
    return this.get<Country[]>('/country');
  }

  async getRecentAddresses(): Promise<ApiResponse<Address[]>> {
    return this.get<Address[]>('/getrecentaddress');
  }

  // ============================================================================
  // ORDER API
  // ============================================================================

  async createOrder(data: CreateOrderRequest): Promise<ApiResponse<Order>> {
    return this.post<Order>('/orders', data);
  }

  async getOrders(params?: QueryParams): Promise<ApiResponse<Order[]>> {
    return this.get<Order[]>('/orders', { params });
  }

  async getOrderById(id: number): Promise<ApiResponse<Order>> {
    return this.get<Order>(`/orders/show/${id}`);
  }

  async getOrderHistory(params?: QueryParams): Promise<ApiResponse<Order[]>> {
    return this.get<Order[]>('/orders/history', { params });
  }

  async cancelOrder(data: CancelOrderRequest): Promise<ApiResponse<void>> {
    return this.post<void>('/orders/cancel', data);
  }

  async verifyParcel(data: {
    order_id: number;
    otp: string;
  }): Promise<ApiResponse<void>> {
    return this.post<void>('/orders/verify-parcel', data);
  }

  async markDelivered(data: {
    order_id: number;
    otp: string;
  }): Promise<ApiResponse<void>> {
    return this.post<void>('/orders/parcel-delivered', data);
  }

  async closeRide(orderId: number): Promise<ApiResponse<void>> {
    return this.post<void>('/orders/close-ride', { order_id: orderId });
  }

  async resendOrder(orderId: number): Promise<ApiResponse<void>> {
    return this.get<void>(`/orders/resend-order/${orderId}`);
  }

  // ============================================================================
  // BID API
  // ============================================================================

  async createBid(data: Partial<Bid>): Promise<ApiResponse<Bid>> {
    return this.post<Bid>('/bids', data);
  }

  async getBidsByOrder(orderId: number): Promise<ApiResponse<Bid[]>> {
    return this.get<Bid[]>(`/orders/get-bid-by-order/${orderId}`);
  }

  async approveBid(data: ApproveBidRequest): Promise<ApiResponse<void>> {
    return this.get<void>(`/bids/approve-bid/${data.bid_id}`);
  }

  async getSelectedDriver(orderId: number): Promise<ApiResponse<User>> {
    return this.get<User>(`/orders/get-selected-driver/${orderId}`);
  }

  // ============================================================================
  // PAYMENT & WALLET API
  // ============================================================================

  async getWalletBalance(): Promise<ApiResponse<number>> {
    return this.get<number>('/users/wallet-balance');
  }

  async getWalletHistory(
    params?: QueryParams
  ): Promise<ApiResponse<WalletTransaction[]>> {
    return this.get<WalletTransaction[]>('/payments/history', { params });
  }

  async addMoneyToWallet(data: AddMoneyRequest): Promise<ApiResponse<Payment>> {
    return this.post<Payment>('/user-wallet-transcation', data);
  }

  async payFromWallet(data: {
    order_id: number;
    amount: number;
  }): Promise<ApiResponse<void>> {
    return this.post<void>('/users/pay-from-balance', data);
  }

  // ============================================================================
  // FEEDBACK API
  // ============================================================================

  async submitFeedback(
    data: SubmitFeedbackRequest
  ): Promise<ApiResponse<Feedback>> {
    return this.post<Feedback>('/feedback', data);
  }

  async getFeedback(orderId: number): Promise<ApiResponse<Feedback>> {
    return this.get<Feedback>(`/feedback/${orderId}`);
  }

  // ============================================================================
  // NOTIFICATION API
  // ============================================================================

  async getNotifications(
    params?: QueryParams
  ): Promise<ApiResponse<Notification[]>> {
    return this.get<Notification[]>('/notifications', { params });
  }

  async markNotificationAsRead(
    notificationId: number
  ): Promise<ApiResponse<void>> {
    return this.put<void>(`/notifications/${notificationId}/read`);
  }

  async sendNotification(
    data: SendNotificationRequest
  ): Promise<ApiResponse<void>> {
    return this.post<void>('/notifications', data);
  }

  // ============================================================================
  // COMFY SHUTTLE API
  // ============================================================================

  async getComfyBanner(): Promise<ApiResponse<Banner[]>> {
    return this.get<Banner[]>('/banners/comfyshuttle');
  }

  async getComfyScheduleTimes(data: {
    from: number | string;
    to: number | string;
  }): Promise<ApiResponse<ShuttleScheduleTime[]>> {
    return this.post<ShuttleScheduleTime[]>('/schedule', data);
  }

  async getComfyPickupPoints(
    cityId: number | string
  ): Promise<ApiResponse<ShuttlePickupPoint[]>> {
    return this.get<ShuttlePickupPoint[]>(`/get-pick-up-points/${cityId}`);
  }

  async getComfyBusSchedules(data: {
    from: number | string;
    to: number | string;
    date: string; // DD-MM-YYYY
  }): Promise<ApiResponse<ShuttleSchedule[]>> {
    return this.post<ShuttleSchedule[]>('/getScheduleBus', data);
  }

  async getComfySeatLayout(
    busId: number | string
  ): Promise<ApiResponse<ShuttleSeatSelection[]>> {
    return this.get<ShuttleSeatSelection[]>(`/vehicle/seatSelection/${busId}`);
  }

  async checkComfyCoupon(data: {
    couponcode: string;
    date: string; // YYYY-MM-DD
    route_id: number | string;
    android_version?: string;
  }): Promise<ApiResponse<ShuttleCouponCheckResponse>> {
    return this.post<ShuttleCouponCheckResponse>('/coupon/checkCouponCode', data);
  }

  async bookComfyShuttle(
    data: ShuttleBookingRequest
  ): Promise<ApiResponse<ShuttleBookingResponse>> {
    return this.post<ShuttleBookingResponse>('/shuttle', data);
  }

  async getComfyBookings(
    params?: QueryParams
  ): Promise<ApiResponse<ShuttleBooking[]>> {
    return this.get<ShuttleBooking[]>('/customer/bookings/list', { params });
  }

  // ============================================================================
  // TOURS API
  // ============================================================================

  async getTours(params?: QueryParams): Promise<ApiResponse<Tour[]>> {
    return this.get<Tour[]>('/tours', { params });
  }

  async getTourById(id: number): Promise<ApiResponse<Tour>> {
    return this.get<Tour>(`/tours/${id}`);
  }

  async getTourDestinations(
    params?: QueryParams
  ): Promise<ApiResponse<TourDestination[]>> {
    return this.get<TourDestination[]>('/tours-destinations', { params });
  }

  async toursform(data: {
    days: number;
    feature: boolean;
    from_place: number;
    include_toll: boolean;
    mobile_number: string;
    name: string;
    start_date: string;
    to_place: number;
    vehical_sub_type_id: number | string;
    via_place: string;
    adults_checked: number;
    children_checked: number;
  }): Promise<ApiResponse<any>> {
    return this.post<any>('/tours', data);
  }

  // ============================================================================
  // ON-CALL API
  // ============================================================================

  async getOnCallPackages(): Promise<ApiResponse<OnCallPackage[]>> {
    return this.get<OnCallPackage[]>('/packages');
  }
  async getOnCallDrivers(data: {
    lat: number;
    lng: number;
    subtype: number;
  }): Promise<ApiResponse<User[]>> {
    return this.post<User[]>('/on-call/get-drivers', data);
  }

  async callDriver(data: {
    driver_id: number;
    customer_id: number;
  }): Promise<ApiResponse<OnCallBooking>> {
    return this.get<OnCallBooking>(
      `/on-call/call/${data.driver_id}/${data.customer_id}`
    );
  }

  async submitOnCallFeedback(data: {
    booking_id: number;
    rating: number;
    remarks?: string;
  }): Promise<ApiResponse<void>> {
    const payload = {
      rating: data.rating,
      remarks: data.remarks,
    };
    return this.post<void>(`/on-call/submit-feedback/${data.booking_id}`, payload);
  }

  async notifyOnCallDrivers(data: {
    driver_ids: Array<number>;
  }): Promise<ApiResponse<void>> {
    return this.post<void>('/on-call/driverNotification', data);
  }

  // ============================================================================
  // SETTINGS & CONFIGURATION API
  // ============================================================================

  async getSettings(): Promise<ApiResponse<any>> {
    return this.get<any>('/settings');
  }

  async getBanners(type?: string): Promise<ApiResponse<Banner[]>> {
    const url = type ? `/banners/getbanner/${type}` : '/banners';
    return this.get<Banner[]>(url);
  }

  async getAdBanners(): Promise<ApiResponse<Banner[]>> {
    return this.get<Banner[]>('/banners/ads');
  }

  async getFAQs(): Promise<ApiResponse<FAQ[]>> {
    return this.get<FAQ[]>('/faqs');
  }

  async getCancellationReasons(): Promise<ApiResponse<CancellationReason[]>> {
    return this.get<CancellationReason[]>('/reasons');
  }

  // ============================================================================
  // GOODS TYPE API
  // ============================================================================

  async getGoodsTypes(): Promise<ApiResponse<GoodsType[]>> {
    return this.get<GoodsType[]>('/goods-type');
  }

  // ============================================================================
  // RATE PREDICTOR API
  // ============================================================================

  async getRatePredictor(data: {
    from_lat: number;
    from_lng: number;
    to_lat: number;
    to_lng: number;
    city_id?: number;
  }): Promise<ApiResponse<any>> {
    // Response can be either:
    // 1. { vehicle_types: [...], total_distance_km, total_duration_minutes } (Auto/Taxi)
    // 2. { city, no_of_km, total_travel_time, fares: [{ min_price, max_price, vehicle_model: {...} }] } (Loading)
    return this.post<any>('/city/rate-predictor', data);
  }

  // ============================================================================
  // FILE UPLOAD API
  // ============================================================================

  async uploadFile(file: File, type: string): Promise<ApiResponse<{ url: string; filename: string }>> {
    console.log('[API Client] uploadFile called');
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      uploadType: type
    });
    
    const formData = new FormData();
    formData.append('file', file);  // Try 'image' field name
    formData.append('type', type);

        formData.forEach((value, key) => {
      console.log(key, value, 'this is the form data after appending');
    });

    // console.log('[API Client] FormData prepared with fields: image, type');
    // console.log('[API Client] Sending POST request to /attachments/upload...');
    
    try {
      // Don't set Content-Type - let browser set it with boundary
      const response = await this.client.post<ApiResponse<{ url: string; filename: string }>>(
        '/attachments/upload',
        formData,
        {
          headers: {
            // Let the browser set boundary automatically
            'Content-Type': 'multipart/form-data',
          },
          transformRequest: (data) => data, // prevent Axios from stringifying
        }
      );


      // console.log('[API Client] ✅ Upload SUCCESS!');
      // console.log('[API Client] Full response:', JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error: any) {
      console.error('[API Client] Upload error details:');
      console.error('- Status:', error.response?.status);
      console.error('- Status Text:', error.response?.statusText);
      console.error('- Response Data:', error.response?.data);
      console.error('- Response Headers:', error.config?.headers);
      console.error('- Full error:', error);
      throw error;
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/helloworld');
      return true;
    } catch {
      return false;
    }
  }

  getBaseURL(): string {
    return API_BASE_URL;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const apiClient = new ApiClient();

export default apiClient;

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export { ApiClient };
export type { AxiosRequestConfig, AxiosResponse };
